import clsx from "clsx";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import {
  Box,
  ClickAwayListener,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import IconLink from "../../common/FormControl/IconLink";
import ColumnSearch from "./ColumnSearch";
import ColumnSearchDate from "./ColumnSearchDate";

const useStyles = makeStyles(theme => ({
  popoverText: {
    fontSize: 12,
    marginBottom: 5,
  },
  popoverSort: {
    fontSize: 11,
    "& span": {
      fontWeight: 600,
    },
  },
  iconButtonSort: {
    padding: "2px 4px",
    color: theme.palette.common.link,
    marginLeft: 5,
  },
  icon: {
    width: "unset",
    height: "unset",
    overflow: "unset",
    fontSize: 12,
  },
  iconSort: {
    fontSize: 11,
  },
  sortName: {
    cursor: "pointer",
    fontWeight: 800,
  },
  activeFilter: {
    color: theme.palette.primary.main,
  },
}));

const SortPopover = ({
  name,
  ascending,
  sortfield,
  searchValues,
  columnFilterValues,
  toggleHeader,
  onClear,
  onCloseHeader,
  onColumnFilter,
  onDateChange,
  onSearch,
  onSortField,
  onToggleHeader,
}) => {
  const classes = useStyles();

  const byName = `${name}By`;
  const byDate = `${name}Date`;

  const isNameFiltered = searchValues?.sortfield === byName;
  const isDateFiltered = searchValues?.sortfield === byDate;

  return (
    <>
      <Typography className={classes.popoverText}>
        <FormattedMessage id="sorting.by" />:
      </Typography>
      <Box className={classes.popoverSort}>
        <IconLink
          icon="fa fa-search"
          customClass={clsx(classes.colSearchIcon, {
            [classes.activeFilter]: isNameFiltered,
          })}
          onClick={() => onToggleHeader(byName)}
        />
        <TableSortLabel
          active={sortfield === byName}
          direction={sortfield === byName && ascending ? "asc" : "desc"}
          onClick={onSortField(byName)}
        >
          <FormattedMessage id="sorting.name" />
        </TableSortLabel>
        {toggleHeader[byName] && (
          <ClickAwayListener onClickAway={onCloseHeader}>
            <div>
              <ColumnSearch
                name={byName}
                value={columnFilterValues?.value || ""}
                onChange={onColumnFilter}
                onClear={onClear}
                onSearch={onSearch}
              />
            </div>
          </ClickAwayListener>
        )}
      </Box>
      <Box className={classes.popoverSort}>
        <IconLink
          icon="fa fa-search"
          customClass={clsx(classes.colSearchIcon, {
            [classes.activeFilter]: isDateFiltered,
          })}
          onClick={() => onToggleHeader(byDate)}
        />
        <TableSortLabel
          active={sortfield === byDate}
          direction={sortfield === byDate && ascending ? "asc" : "desc"}
          onClick={onSortField(byDate)}
        >
          <FormattedMessage id="product.date" />
        </TableSortLabel>
        {toggleHeader[byDate] && (
          <ClickAwayListener onClickAway={onCloseHeader}>
            <div>
              <ColumnSearchDate
                name={byDate}
                value={columnFilterValues?.date}
                onClear={onClear}
                onDateChange={onDateChange}
                onSearch={onSearch}
              />
            </div>
          </ClickAwayListener>
        )}
      </Box>
    </>
  );
};

SortPopover.propTypes = {
  name: PropTypes.string.isRequired,
  ascending: PropTypes.bool.isRequired,
  sortfield: PropTypes.string.isRequired,
  searchValues: PropTypes.shape({
    sortfield: PropTypes.string,
    value: PropTypes.string,
  }),
  columnFilterValues: PropTypes.shape({
    sortfield: PropTypes.string,
    value: PropTypes.string,
    date: PropTypes.instanceOf(Date),
  }).isRequired,
  toggleHeader: PropTypes.objectOf(PropTypes.bool).isRequired,
  onClear: PropTypes.func.isRequired,
  onCloseHeader: PropTypes.func.isRequired,
  onColumnFilter: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onSortField: PropTypes.func.isRequired,
  onToggleHeader: PropTypes.func.isRequired,
};

export default SortPopover;
